import { TripData, HotelPricing } from "@/hooks/useTrips";
import { FlightCard } from "@/components/trips/items/FlightCard";
import { HotelCard } from "@/components/trips/items/HotelCard";
import { CarCard } from "@/components/trips/items/CarCard";
import { ActivityCard } from "@/components/trips/items/ActivityCard";
import { CruiseCard } from "@/components/trips/items/CruiseCard";
import type { ViewMode } from "@/components/itinerary/ItineraryPreview";
import { safeFormat } from "@/lib/formatDateTime";
import { FlightSegment, HotelReservation, CarReservation, Activity, CruiseReservation } from "@/hooks/useTrips";

function extractDateFromISO(isoString: string): string | null {
  const match = isoString.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

function getLocalDate(explicitDate?: string, timeField?: string): string {
  if (explicitDate && /^\d{4}-\d{2}-\d{2}/.test(explicitDate)) {
    return explicitDate.slice(0, 10);
  }
  if (timeField && timeField.includes("T")) {
    const extracted = extractDateFromISO(timeField);
    if (extracted) return extracted;
  }
  return "";
}

function parseDateTimeForSorting(date?: string, time?: string): Date {
  const localDate = getLocalDate(date, time);
  if (!localDate) return new Date(0);

  try {
    let timeStr = "00:00";
    if (time) {
      const isoTimeMatch = time.match(/T(\d{2}:\d{2})/);
      if (isoTimeMatch) {
        timeStr = isoTimeMatch[1];
      } else {
        const simpleTimeMatch = time.match(/^(\d{1,2}:\d{2})/);
        if (simpleTimeMatch) {
          timeStr = simpleTimeMatch[1].padStart(5, "0");
        }
      }
    }

    const [year, month, day] = localDate.split("-").map(Number);
    const [hours, minutes] = timeStr.split(":").map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  } catch {
    return new Date(0);
  }
}

type TimelineItem = {
  id: string;
  type: "flight" | "hotel-checkin" | "hotel-checkout" | "car-pickup" | "car-dropoff" | "cruise" | "activity";
  date: string;
  sortTime: Date;
  data: FlightSegment | HotelReservation | CarReservation | Activity | CruiseReservation;
  pricing?: unknown;
};

function ItineraryByDate({ tripData }: { tripData: TripData }) {
  const items: TimelineItem[] = [];

  tripData.flights?.forEach((flight, index) => {
    const localDate = getLocalDate(flight.departure_date, flight.departure_time);
    if (!localDate) return;
    items.push({
      id: `flight-${index}`,
      type: "flight",
      date: localDate,
      sortTime: parseDateTimeForSorting(flight.departure_date, flight.departure_time),
      data: flight,
    });
  });

  tripData.hotels?.forEach((hotel, index) => {
    const checkInDate = getLocalDate(hotel.check_in_date, hotel.check_in_time);
    const checkOutDate = getLocalDate(hotel.check_out_date, hotel.check_out_time);
    if (checkInDate) {
      items.push({
        id: `hotel-in-${index}`,
        type: "hotel-checkin",
        date: checkInDate,
        sortTime: parseDateTimeForSorting(hotel.check_in_date, hotel.check_in_time),
        data: hotel,
        pricing: tripData.hotel_pricings?.find((p) => p.hotel_name === hotel.hotel_name),
      });
    }
    if (checkOutDate) {
      items.push({
        id: `hotel-out-${index}`,
        type: "hotel-checkout",
        date: checkOutDate,
        sortTime: parseDateTimeForSorting(hotel.check_out_date, hotel.check_out_time),
        data: hotel,
      });
    }
  });

  tripData.cars?.forEach((car, index) => {
    const pickupDate = getLocalDate(car.pickup_date, car.pickup_time);
    const dropoffDate = getLocalDate(car.dropoff_date, car.dropoff_time);
    if (pickupDate) {
      items.push({
        id: `car-pickup-${index}`,
        type: "car-pickup",
        date: pickupDate,
        sortTime: parseDateTimeForSorting(car.pickup_date, car.pickup_time),
        data: car,
      });
    }
    if (dropoffDate) {
      items.push({
        id: `car-dropoff-${index}`,
        type: "car-dropoff",
        date: dropoffDate,
        sortTime: parseDateTimeForSorting(car.dropoff_date, car.dropoff_time),
        data: car,
      });
    }
  });

  tripData.activities?.forEach((activity, index) => {
    const activityDate = getLocalDate(activity.date, activity.time);
    if (!activityDate) return;
    items.push({
      id: `activity-${index}`,
      type: "activity",
      date: activityDate,
      sortTime: parseDateTimeForSorting(activity.date, activity.time),
      data: activity,
    });
  });

  tripData.cruises?.forEach((cruise, index) => {
    const cruiseDate = getLocalDate(cruise.departure_date, cruise.departure_time);
    if (!cruiseDate) return;
    items.push({
      id: `cruise-${index}`,
      type: "cruise",
      date: cruiseDate,
      sortTime: parseDateTimeForSorting(cruise.departure_date, cruise.departure_time),
      data: cruise,
    });
  });

  items.sort((a, b) => a.sortTime.getTime() - b.sortTime.getTime());

  const itemsByDate = items.reduce((acc, item) => {
    if (!acc[item.date]) acc[item.date] = [];
    acc[item.date].push(item);
    return acc;
  }, {} as Record<string, TimelineItem[]>);

  const sortedDates = Object.keys(itemsByDate).sort();

  return (
    <div className="space-y-8">
      {sortedDates.map((date) => (
        <section key={date} className="mb-8">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="gradient-accent text-white px-4 py-2 rounded-lg">
              <div className="text-sm">{safeFormat(date, "EEE")}</div>
              <div className="text-lg font-semibold">{safeFormat(date, "MMM d")}</div>
            </div>
            <h2 className="text-xl font-semibold">{safeFormat(date, "MMMM d, yyyy")}</h2>
          </div>

          <div className="space-y-4">
            {itemsByDate[date].map((item) => {
              if (item.type === "flight") {
                return (
                  <FlightCard
                    key={item.id}
                    flight={item.data as FlightSegment}
                    allFlights={tripData.flights || []}
                    readOnly
                    tripPassengerName={tripData.passenger_name}
                  />
                );
              }
              if (item.type === "hotel-checkin" || item.type === "hotel-checkout") {
                return (
                  <HotelCard
                    key={item.id}
                    hotel={item.data as HotelReservation}
                    pricing={item.pricing as HotelPricing | undefined}
                    readOnly
                  />
                );
              }
              if (item.type === "car-pickup" || item.type === "car-dropoff") {
                return <CarCard key={item.id} car={item.data as CarReservation} readOnly />;
              }
              if (item.type === "activity") {
                return <ActivityCard key={item.id} activity={item.data as Activity} readOnly />;
              }
              if (item.type === "cruise") {
                return <CruiseCard key={item.id} cruise={item.data as CruiseReservation} readOnly />;
              }
              return null;
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

export function ItineraryCardsView({ tripData, viewMode }: { tripData: TripData | null; viewMode: ViewMode }) {
  if (!tripData) return null;

  // Sort flights by departure date/time
  const sortedFlights = tripData.flights
    ? [...tripData.flights].sort((a, b) =>
        parseDateTimeForSorting(a.departure_date, a.departure_time).getTime() -
        parseDateTimeForSorting(b.departure_date, b.departure_time).getTime()
      )
    : [];

  // Sort hotels by check-in date/time
  const sortedHotels = tripData.hotels
    ? [...tripData.hotels].sort((a, b) =>
        parseDateTimeForSorting(a.check_in_date, a.check_in_time).getTime() -
        parseDateTimeForSorting(b.check_in_date, b.check_in_time).getTime()
      )
    : [];

  // Sort cars by pickup date/time
  const sortedCars = tripData.cars
    ? [...tripData.cars].sort((a, b) =>
        parseDateTimeForSorting(a.pickup_date, a.pickup_time).getTime() -
        parseDateTimeForSorting(b.pickup_date, b.pickup_time).getTime()
      )
    : [];

  // Sort activities by date/time
  const sortedActivities = tripData.activities
    ? [...tripData.activities].sort((a, b) =>
        parseDateTimeForSorting(a.date, a.time).getTime() -
        parseDateTimeForSorting(b.date, b.time).getTime()
      )
    : [];

  // Sort cruises by departure date
  const sortedCruises = tripData.cruises
    ? [...tripData.cruises].sort((a, b) =>
        parseDateTimeForSorting(a.departure_date, undefined).getTime() -
        parseDateTimeForSorting(b.departure_date, undefined).getTime()
      )
    : [];

  return (
    <>
      {viewMode === "by-type" && (
        <>
          {sortedFlights.length > 0 && (
            <section className="mb-8">
              <h2 className="font-display text-xl font-semibold mb-4">Flights</h2>
              <div className="space-y-4">
                {sortedFlights.map((flight, index) => (
                  <FlightCard
                    key={index}
                    flight={flight}
                    allFlights={tripData.flights || []}
                    readOnly
                    tripPassengerName={tripData.passenger_name}
                  />
                ))}
              </div>
            </section>
          )}

          {sortedHotels.length > 0 && (
            <section className="mb-8">
              <h2 className="font-display text-xl font-semibold mb-4">Accommodations</h2>
              <div className="space-y-4">
                {sortedHotels.map((hotel, index) => {
                  const pricing = tripData.hotel_pricings?.find(
                    (p) => p.hotel_name === hotel.hotel_name && p.check_in_date === hotel.check_in_date
                  );
                  return <HotelCard key={index} hotel={hotel} pricing={pricing} readOnly />;
                })}
              </div>
            </section>
          )}

          {sortedCars.length > 0 && (
            <section className="mb-8">
              <h2 className="font-display text-xl font-semibold mb-4">Car Rentals</h2>
              <div className="space-y-4">
                {sortedCars.map((car, index) => (
                  <CarCard key={index} car={car} readOnly />
                ))}
              </div>
            </section>
          )}

          {sortedActivities.length > 0 && (
            <section className="mb-8">
              <h2 className="font-display text-xl font-semibold mb-4">Activities & Experiences</h2>
              <div className="space-y-4">
                {sortedActivities.map((activity, index) => (
                  <ActivityCard key={index} activity={activity} readOnly />
                ))}
              </div>
            </section>
          )}

          {sortedCruises.length > 0 && (
            <section className="mb-8">
              <h2 className="font-display text-xl font-semibold mb-4">Cruises</h2>
              <div className="space-y-4">
                {sortedCruises.map((cruise, index) => (
                  <CruiseCard key={index} cruise={cruise} readOnly />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {viewMode === "by-date" && <ItineraryByDate tripData={tripData} />}
    </>
  );
}
